'use client';

import { useMemo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { blend } from '@mui/system/colorManipulator';
import {
  categoryBorderColor,
  categoryCellBackground,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { black, blue, purple, slate, white } from '@/lib/colours';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';

/**
 * Borders, fills, and shadows for the mating-results tables that must track the
 * active color scheme. Blends use literal paper tones (theme dark/light
 * `background.paper`) — not `theme.palette.background.paper` from `useTheme()`,
 * which can stay on the default palette under `colorSchemes` + CSS variables and
 * produce pale “white” mixes.
 *
 * EBV highlight (`display === 2`): body cells match {@link PublicSearchDataGrid}
 * (purple + alpha in dark); sticky header / breed-average highlight cells use
 * opaque blends so rows don’t show through when scrolling.
 */
export function useMatingTableChrome() {
  const theme = useTheme();
  const { isDarkMode } = useResolvedColorScheme();

  return useMemo(() => {
    const divider = theme.palette.divider;
    /** Matches `theme.ts` light/dark `background.paper` for reliable opaque blends */
    const paperSurface = isDarkMode ? slate[800] : white[50];
    const cellBorder = `1px solid ${divider}`;
    const pairGroupBorder = `1px solid ${divider}`;
    const primaryRowBorderColor = isDarkMode ? blue[300] : blue[600];
    /**
     * EBV index highlight (display === 2) — matches the grid's
     * `.MuiDataGrid-*.ebv-highlighted`: a solid purple header, a 3px purple
     * outline on the index column edges, and bold purple text in the body
     * cells (no fill).
     */
    const ebvHighlightBorder = `3px solid ${
      isDarkMode ? alpha(purple[300], 0.5) : purple[300]
    }`;
    /** Header cells: solid purple (reduced to alpha in dark to avoid glare) */
    const ebvHeaderHighlightBg = isDarkMode
      ? alpha(purple[300], 0.25)
      : purple[300];
    const primaryRowBaseBg = isDarkMode
      ? blend(paperSurface, blue[600], 0.5)
      : blue[50];
    const epaRowBg = isDarkMode
      ? alpha(theme.palette.common.white, 0.06)
      : slate[100];
    const stickyColShadow = isDarkMode
      ? `2px 0 8px -2px ${alpha(black[900], 0.55)}`
      : `2px 0 4px -2px ${alpha(black[900], 0.1)}`;
    const headerBottom = `2px solid ${divider}`;
    /** Opaque sticky fills (same literal paper as EBV highlight sticky) — avoids body showing through. */
    const categoryHeaderBackground = (tint: string) =>
      isDarkMode
        ? blend(paperSurface, tint, 0.4)
        : blend(paperSurface, tint, 0.48);
    // Subtle category fill + border for non-index EBV cells — the same theming
    // the animal list grid applies, so a category looks identical across views.
    const categoryCellBg = (tint: string) =>
      categoryCellBackground(tint, paperSurface, isDarkMode);
    const categoryBorder = (tint: string) =>
      `2px solid ${categoryBorderColor(tint, isDarkMode)}`;
    const pivotHeaderForeground = isDarkMode ? slate[50] : slate[900];
    return {
      cellBorder,
      pairGroupBorder,
      primaryRowBaseBg,
      primaryRowBorderColor,
      ebvHighlightBorder,
      ebvHeaderHighlightBg,
      epaRowBg,
      stickyColShadow,
      headerBottom,
      paperSurface,
      categoryHeaderBackground,
      categoryCellBg,
      categoryBorder,
      pivotHeaderForeground,
    };
  }, [theme, isDarkMode]);
}

export type MatingTableChrome = ReturnType<typeof useMatingTableChrome>;
